import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { io } from "socket.io-client";
import BASE_URL from "../../baseUrl";
import { getAuthHeaders, getUserInfo } from "./chatUtils";
import "./chat.css";

const AgentInbox = () => {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [typing, setTyping] = useState(false);
  const socketRef = useRef(null);
  const bottomRef = useRef(null);
  const user = useMemo(() => getUserInfo(), []);

  const loadSessions = async () => {
    const res = await axios.get(`${BASE_URL}/api/rechat/agent/sessions`, {
      headers: { ...getAuthHeaders() },
    });
    setSessions(res?.data?.sessions || []);
    if (!activeSession && res?.data?.sessions?.length) {
      setActiveSession(res.data.sessions[0]);
    }
  };

  const loadMessages = async (sessionId) => {
    if (!sessionId) return;
    const res = await axios.get(`${BASE_URL}/api/rechat/session/${sessionId}/messages`, {
      params: { page: 1, limit: 200 },
      headers: { ...getAuthHeaders() },
    });
    setMessages(res?.data?.messages || []);
    await axios.patch(
      `${BASE_URL}/api/rechat/session/${sessionId}/read`,
      {},
      { headers: { ...getAuthHeaders() } }
    );
  };

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    if (!activeSession?._id) return;
    loadMessages(activeSession._id);
  }, [activeSession?._id]);

  useEffect(() => {
    const socket = io(BASE_URL, { transports: ["websocket"] });
    socketRef.current = socket;

    socket.on("rechat:new-message", (payload) => {
      if (payload?.sessionId === activeSession?._id) {
        setMessages((prev) => [...prev, payload.message]);
      }
      loadSessions();
    });

    socket.on("rechat:typing", (payload) => {
      if (payload?.sessionId === activeSession?._id && payload.by === "visitor") {
        setTyping(!!payload.isTyping);
      }
    });

    return () => socket.disconnect();
  }, [activeSession?._id]);

  useEffect(() => {
    if (!socketRef.current || !activeSession?._id) return;
    socketRef.current.emit("rechat:join", {
      sessionId: activeSession._id,
      token: user?.token || "",
    });
  }, [activeSession?._id, user?.token]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  const send = async () => {
    const value = text.trim();
    if (!value || !activeSession?._id) return;
    setText("");
    socketRef.current.emit("rechat:send", { text: value }, async (ack) => {
      if (!ack?.ok) {
        const res = await axios.post(
          `${BASE_URL}/api/rechat/session/${activeSession._id}/message`,
          { text: value },
          { headers: { ...getAuthHeaders() } }
        );
        if (res?.data?.message) setMessages((prev) => [...prev, res.data.message]);
      }
    });
  };

  return (
    <div className="agent-inbox">
      <div className="agent-inbox-list">
        <h5>Conversations</h5>
        {sessions.map((s) => (
          <button
            key={s._id}
            type="button"
            className={`agent-session-item ${activeSession?._id === s._id ? "active" : ""}`}
            onClick={() => setActiveSession(s)}
          >
            <strong>{s?.listingId?.BasicInformation?.name || "Listing Chat"}</strong>
            <span>{s.lastMessageText || "No messages yet"}</span>
            <small>{new Date(s.lastMessageAt).toLocaleString()}</small>
          </button>
        ))}
      </div>
      <div className="agent-inbox-chat">
        <div className="rechat-header">
          <h5>{activeSession?.listingId?.BasicInformation?.name || "Select a chat"}</h5>
        </div>
        <div className="rechat-body">
          {messages.map((m) => (
            <div key={m._id} className={`rechat-message ${m.senderType === "agent" ? "mine" : "their"}`}>
              <div>{m.text}</div>
              <small>{new Date(m.createdAt).toLocaleString()}</small>
            </div>
          ))}
          {typing ? <div className="rechat-typing">Visitor is typing...</div> : null}
          <div ref={bottomRef} />
        </div>
        <div className="rechat-footer">
          <input
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              socketRef.current?.emit("rechat:typing", { isTyping: true });
            }}
            onBlur={() => socketRef.current?.emit("rechat:typing", { isTyping: false })}
            onKeyDown={(e) => {
              if (e.key === "Enter") send();
            }}
            placeholder="Reply..."
          />
          <button type="button" onClick={send}>
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentInbox;

